import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import Input from '../../components/Input'
import { useLocation, useNavigate } from 'react-router-dom'
import UserPool from './UserPool'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { CognitoUser } from 'amazon-cognito-identity-js'
import Button from '../../components/Button'
import { VERIFY_USER_SCHEMA } from '../../validations/schemas'
import Alert from '../../components/Alert'
import { isNumberRegex } from '../../validations/validationHelpers'
import { useCreateUserMutation } from '../../redux/api/generalApi'
import { pathName } from '../../utilities/commonConstants'
import { verifyToastMessage, verifyUserLabel } from '../../utilities/pageConstants'

const VerifyUser: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [loading, setLoading] = useState(false)
    const [apiError, setApiError] = useState('')
    const [showTimer, setShowTimer] = useState(true)
    const [createUser] = useCreateUserMutation()

    const [minutes, setMinutes] = useState(5)
    const [seconds, setSeconds] = useState(0)

    const startTimer = () => {
        const interval = setInterval(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1)
            }

            if (seconds === 0) {
                if (minutes === 0) {
                    clearInterval(interval)
                } else {
                    setSeconds(59)
                    setMinutes(minutes - 1)
                }
            }
        }, 1000)

        return interval
    }

    useEffect(() => {
        const interval = startTimer()
        return () => {
            clearInterval(interval)
        }
        // eslint-disable-next-line
    }, [seconds, minutes])

    const handleCodeResend = () => {
        const username = location.state?.Username

        if (!username) {
            console.error('Username not found in location state.')
            return
        }

        const userData = {
            Username: username,
            Pool: UserPool
        }

        const cognitoUserData = new CognitoUser(userData)
        cognitoUserData.resendConfirmationCode(function (err: any, result) {
            if (err) {
                setApiError(err.message)
                if (err.code === 'LimitExceededException') {
                    setApiError('Maximum number of attempts exceeded. Please try again after sometime.')
                    setShowTimer(false)
                }
                return
            }
            toast.success(verifyToastMessage?.verificationCode)
            setMinutes(5)
            setSeconds(0)
            setShowTimer(true)
            startTimer()
        })
    }

    const handleVerifyUser = async (values: any) => {
        setLoading(formik.isSubmitting)
        const username = location.state?.Username
        const { code } = values
        if (!username) {
            setLoading(!formik.isSubmitting)
            console.error('Username not found in location state.')
            return
        }

        const userData = {
            Username: username,
            Pool: UserPool
        }

        const cognitoUserData = new CognitoUser(userData)
        cognitoUserData.confirmRegistration(code.toString(), true, async function (err, result) {
            if (err) {
                setLoading(!formik.isSubmitting)
                if (err.code === 'CodeMismatchException') {
                    setApiError('Code is invalid.')
                } else if (err.code === 'LimitExceededException') {
                    setApiError('Maximum number of attempts exceeded. Please try again after sometime.')
                    setShowTimer(false)
                } else {
                    setApiError(err.message)
                }
                return
            }
            const mode = sessionStorage.getItem('mode')
            if (result) {
                const payload = {
                    username: username,
                    userPoolId: process.env.REACT_APP_USER_POOL_ID,
                    flowType: mode
                }
                await createUser(payload)
                toast.success(verifyToastMessage?.registeredSuccessfully, { autoClose: false })
                setLoading(!formik.isSubmitting)
                navigate(pathName?.signIn)
            }
        })
    }

    const handleVerificationCodeKeyPress = (event: { key: string; ctrlKey: boolean; preventDefault: () => void }) => {
        const isValidInput = isNumberRegex.test(event.key) || event.key === 'Backspace'
        const isPasteEvent = event.key === 'v' || event.key === 'V'

        if (!isValidInput && (!isPasteEvent || !event.ctrlKey)) {
            event.preventDefault()
        }
    }

    const formik = useFormik({
        initialValues: { code: '' },
        validationSchema: VERIFY_USER_SCHEMA,
        onSubmit: handleVerifyUser
    })
    const { values, errors, touched, handleChange, handleBlur } = formik
    return (
        <React.Fragment>
            <div className='pb-8'>
                <div className='space-y-4'>
                    <h1 className='text-3xl font-bold text-black'>{verifyUserLabel?.codeVerification}</h1>
                </div>
                <form noValidate className='space-y-4' autoComplete='off' onSubmit={formik.handleSubmit}>
                    <div> {apiError && <Alert apiError={apiError} setApiError={setApiError} />}</div>
                    <Input
                        id='code'
                        name='code'
                        type='text'
                        maxLength={6}
                        isRequired={true}
                        onKeyDown={handleVerificationCodeKeyPress}
                        label='Code'
                        labelClass='flex block mb-2 text-base font-medium text-primary text-left'
                        value={values.code}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Verification code'
                        className={`flex border border-secondary text-black text-base rounded-lg w-full px-4 py-2.5 font-normal shadow-sm placeholder:text-secondary focus:outline-none focus:border-primary focus:ring-1`}
                    />
                    {errors.code && touched.code ? <div className='block mt-2 text-danger text-sm text-left'>{errors.code}</div> : null}

                    <Button
                        className={`w-full bg-primary text-white font-medium rounded-lg text-lg px-5 py-2.5 text-center shadow-md ${
                            loading && 'disabled'
                        }`}
                        disabled={loading}
                        type='submit'
                    >
                        {loading ? (
                            <div className='flex justify-center items-center'>
                                <div className='border-gray-300 h-7 w-7 ml-3 animate-spin rounded-full border-4 border-t-blue-600' />
                            </div>
                        ) : (
                            'Verify'
                        )}
                    </Button>
                    <div>
                        {showTimer && (
                            <p>
                                {verifyUserLabel?.timeRemaining} {minutes < 10 ? `0${minutes}` : minutes}:
                                {seconds < 10 ? `0${seconds}` : seconds}
                            </p>
                        )}
                        {!showTimer ? (
                            ''
                        ) : (
                            <>
                                <p>{verifyUserLabel?.receiveCode}</p>
                                <button
                                    onClick={handleCodeResend}
                                    type='button'
                                    disabled={seconds > 0 || minutes > 0}
                                    className={`btn d-flex justify-content-center text-primary underline ${
                                        seconds > 0 || minutes > 0 ? 'disabled' : ''
                                    }`}
                                >
                                    {' '}
                                    {verifyUserLabel?.resendCode}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </React.Fragment>
    )
}

export default VerifyUser
